/**
 * Permission-driven access control for the HRM frontend.
 *
 * The auth-service embeds canonical `module.feature.action` permission codes in the
 * JWT (products[HRM].permissions). These mirror the backend route guards. We gate nav
 * items, routes, and actions against those codes so each role only sees what it is
 * allowed — the same pattern the Central Hub ERP console uses.
 *
 * Visibility semantics for a page:
 * - Mapped to permission codes → user needs ANY of those codes
 * - Listed in {@link PUBLIC_SELF_SERVICE_PAGES} → every authenticated HRM user
 * - Missing from {@link PAGE_PERMISSIONS} → hidden (deny by default)
 *
 * Org Admins and platform super admins bypass all checks.
 */

/** Holding these signals full product administration → bypass page gates. */
export const ADMIN_SIGNAL_PERMISSIONS = [
  'platform.settings.update',
  'identity.roles.update',
] as const;

/**
 * Pages every authenticated HRM user may open without a feature permission.
 * Keep this list small — everything else must map to a real permission code.
 */
export const PUBLIC_SELF_SERVICE_PAGES = new Set<string>([
  'dashboard',
  'employee-self-service',
  'punch-clock',
  'holiday-calendar',
  'payslips',
  'help-center',
  'notifications',
  'company-calendar',
  'announcements',
]);

/** Normalize a permission string for comparison (canonical codes are already lowercase). */
function norm(code: string): string {
  return code.trim().toLowerCase();
}

/** True when the granted set satisfies any of the required codes. */
export function hasAnyPermission(granted: Iterable<string>, required: string[]): boolean {
  if (required.length === 0) return true;
  const set = granted instanceof Set ? granted : new Set([...granted].map(norm));
  return required.some((r) => set.has(norm(r)));
}

/** True when the granted set satisfies all of the required codes. */
export function hasAllPermissions(granted: Iterable<string>, required: string[]): boolean {
  const set = granted instanceof Set ? granted : new Set([...granted].map(norm));
  return required.every((r) => set.has(norm(r)));
}

/** Whether this user should bypass page-level gating entirely. */
export function isAdminUser(input: {
  permissions: string[];
  isSuperAdmin?: boolean;
  authRole?: string;
}): boolean {
  if (input.isSuperAdmin) return true;
  const role = (input.authRole ?? '').toLowerCase();
  if (role.includes('super') && role.includes('admin')) return true;
  if (role === 'org_admin' || role === 'admin') return true;
  const set = new Set(input.permissions.map(norm));
  // Org Admin HRM pool typically includes identity.users.update + settings.
  if (set.has('identity.users.update') && set.has('platform.settings.read')) return true;
  return ADMIN_SIGNAL_PERMISSIONS.some((p) => set.has(norm(p)));
}

/**
 * Page key → canonical permission codes (ANY-of).
 * Prefer an explicit code over `[]`. Use {@link PUBLIC_SELF_SERVICE_PAGES} for
 * universal self-service instead of empty arrays.
 */
export const PAGE_PERMISSIONS: Record<string, string[]> = {
  // ── Main ───────────────────────────────────────────────
  dashboard: [],
  'dashboard-hr': ['analytics.insights.read', 'reporting.definitions.read'],

  // ── People ─────────────────────────────────────────────
  'employee-directory': ['employee.records.read'],
  'self-onboard-invites': ['employee.records.create'],
  'employee-self-service': [],
  'my-team': ['employee.records.read', 'leave.requests.delete'],

  // ── Organization ───────────────────────────────────────
  'org-structure': ['organization.departments.read'],
  'legal-entities': ['organization.departments.read'],
  'business-units': ['organization.departments.read'],
  departments: ['organization.departments.read'],
  designations: ['organization.departments.read'],
  locations: ['organization.departments.read'],
  'cost-centers': ['organization.departments.read'],
  'pay-grades': ['organization.departments.read'],
  'pay-bands': ['organization.departments.read'],

  // ── Time & Attendance ──────────────────────────────────
  shifts: ['attendance.policy.read', 'attendance.policy.update'],
  'shift-roster': ['attendance.policy.read', 'attendance.policy.update'],
  'shift-swaps': ['employee_requests.shift_swap.read'],
  attendance: ['attendance.timesheet.read'],
  'punch-clock': [],
  geofence: ['attendance.geofence.read', 'attendance.policy.read'],
  'field-tracker': ['attendance.field_tracker.read'],
  'attendance-summary': ['attendance.timesheet.read'],
  'overtime-management': ['attendance.policy.read', 'attendance.timesheet.export'],
  regularization: [
    'attendance.regularization.read',
    'attendance.regularization.create',
    'attendance.regularization.update',
  ],
  'wfh-settings': ['attendance.wfh.update', 'attendance.policy.update'],
  'wfh-requests': ['attendance.wfh.read'],

  // ── Leave ──────────────────────────────────────────────
  'leave-types': ['leave.policy.read'],
  'leave-settings': ['leave.policy.read'],
  'leave-balance': ['leave.requests.read'],
  'leave-applications': ['leave.requests.read'],
  'leave-approvals': ['leave.requests.delete', 'leave.requests.export'],
  'leave-team-calendar': ['leave.requests.read'],
  'leave-encashment': ['leave.requests.read'],
  'holiday-calendar': [],

  // ── Payroll ────────────────────────────────────────────
  'payroll-dashboard': ['payroll.records.export', 'payroll.run.read'],
  'salary-components': ['payroll.settings.read'],
  'salary-structures': ['payroll.settings.read'],
  'payroll-run': ['payroll.run.read', 'payroll.run.update'],
  payslips: [],
  'loan-management': ['payroll.records.read', 'benefits.programs.read'],

  // ── Recruitment ────────────────────────────────────────
  'job-postings': ['recruitment.openings.read'],
  requisitions: ['recruitment.openings.read'],
  'candidate-pipeline': ['recruitment.openings.read'],
  'resume-parser': ['recruitment.openings.read'],
  'employee-referrals': ['recruitment.openings.read'],
  'interview-schedule': ['recruitment.openings.read'],
  'offer-management': ['recruitment.openings.read'],
  'career-fair': ['recruitment.openings.read'],
  'hiring-analytics': ['recruitment.openings.read'],
  'internal-mobility': ['recruitment.openings.read'],

  // ── Performance ────────────────────────────────────────
  'performance-cycles': ['performance.cycles.read'],
  goals: ['performance.cycles.read'],
  feedback: ['performance.reviews.read', 'performance.cycles.read'],
  'nine-box': ['performance.cycles.delete', 'performance.cycles.export'],
  calibration: ['performance.cycles.delete', 'performance.cycles.export'],
  'competency-framework': ['performance.cycles.read'],
  'pip-tracker': ['performance.reviews.delete', 'performance.reviews.export'],
  'team-performance': ['performance.reviews.export', 'performance.cycles.export'],

  // ── Training ───────────────────────────────────────────
  training: ['learning.programs.read'],
  'training-calendar': ['learning.programs.read'],
  'training-materials': ['learning.programs.read'],
  'training-certificates': ['learning.programs.read'],
  'training-feedback': ['learning.programs.read'],
  'learning-paths': ['learning.programs.read'],
  'mentoring-program': ['learning.programs.read'],

  // ── Management ─────────────────────────────────────────
  expenses: ['expense.claims.read'],
  'travel-management': ['travel.bookings.read', 'employee_requests.travel.read'],
  tickets: ['support.tickets.read'],

  // ── Projects ───────────────────────────────────────────
  projects: ['projects.portfolio.read'],
  timesheets: ['projects.resources.read'],

  // ── Surveys ────────────────────────────────────────────
  surveys: ['engagement.surveys.read'],
  'survey-analytics': ['engagement.surveys.delete', 'engagement.surveys.export'],
  'pulse-survey': ['engagement.surveys.read'],

  // ── Assets ─────────────────────────────────────────────
  assets: ['assets.inventory.read'],
  'asset-categories': ['assets.inventory.read'],
  'asset-requests': ['assets.inventory.read'],
  'asset-vendors': ['assets.inventory.read'],
  'asset-purchases': ['assets.inventory.read'],
  'asset-maintenance': ['assets.inventory.read'],
  'asset-depreciation': ['assets.inventory.read'],
  'asset-licenses': ['assets.inventory.read'],
  'asset-stock': ['assets.inventory.read'],
  'asset-cover': ['assets.inventory.read'],
  'asset-incidents': ['assets.inventory.read'],
  'asset-labels': ['assets.inventory.read'],
  'asset-controls': ['assets.inventory.read'],

  // ── Lifecycle ──────────────────────────────────────────
  onboarding: ['employee.onboarding.read', 'employee.onboarding.update'],
  'onboarding-guide': ['employee.onboarding.read', 'employee.onboarding.update'],
  offboarding: ['employee.offboarding.read'],

  // ── Engagement ─────────────────────────────────────────
  policies: ['policy.documents.read'],
  benefits: ['benefits.programs.read'],
  rewards: ['engagement.programs.read'],
  documents: ['documents.files.read', 'documents.models.read'],
  announcements: [], // PUBLIC_SELF_SERVICE_PAGES
  notifications: [], // PUBLIC_SELF_SERVICE_PAGES
  'company-calendar': [], // PUBLIC_SELF_SERVICE_PAGES
  'grievance-management': ['support.tickets.read'],
  'recognition-wall': ['engagement.programs.read'],
  'meeting-scheduler': ['communications.announcements.read'],
  'employee-engagement': ['engagement.programs.read'],

  // ── Analytics ──────────────────────────────────────────
  reports: ['reporting.definitions.read', 'analytics.insights.read'],
  'workforce-planning': ['workforce.planning.read'],
  'attrition-risk': ['analytics.insights.read'],
  'org-chart': ['employee.records.read'],
  'separation-analysis': ['analytics.insights.read', 'employee.offboarding.read'],

  // ── Compliance ─────────────────────────────────────────
  'compliance-dashboard': ['compliance.audit.read'],
  'contingent-workers': ['workforce.contingent.read'],
  'visa-immigration': ['travel.visa.read'],
  'workplace-safety': ['compliance.audit.read'],
  'risk-register': ['compliance.audit.read'],

  // ── System ─────────────────────────────────────────────
  setup: ['platform.settings.read'],
  settings: ['platform.settings.read'],
  picklists: ['platform.picklists.read'],
  'roles-permissions': ['identity.roles.read'],
  'audit-log': ['compliance.audit.read'],
  'workflow-builder': ['workflow.definitions.read'],
  'email-templates': ['communications.announcements.export', 'platform.settings.read'],
  webhooks: ['integrations.webhooks.read'],
  'ai-chatbot': ['platform.settings.read'],
  'help-center': [],
  'data-import-export': ['platform.settings.update'],
  'dashboard-layout': ['platform.settings.update'],
  'quick-actions': ['employee.records.read'],
  'milestone-tracker': ['projects.portfolio.read'],
};

// Added mappings for pages present in navigation but previously missing from
// PAGE_PERMISSIONS. These tighten UI visibility by requiring an explicit
// canonical permission code instead of falling back to universal access.
// Permissions chosen follow the project's permission naming patterns.
const ADDED_PAGE_PERMISSIONS: Record<string, string[]> = {
  announcements: ['communications.announcements.read'],
  assets: ['assets.inventory.read'],
  attendance: ['attendance.timesheet.read'],
  benefits: ['benefits.programs.read', 'benefits.enrollment.read'],
  calibration: ['performance.cycles.delete', 'performance.cycles.export'],
  dashboard: [],
  departments: ['organization.departments.read'],
  designations: ['organization.departments.read'],
  documents: ['documents.files.read', 'documents.models.read'],
  expenses: ['expense.claims.read'],
  feedback: ['performance.reviews.read', 'performance.cycles.read'],
  geofence: ['attendance.geofence.read'],
  goals: ['performance.cycles.read'],
  locations: ['organization.departments.read'],
  notifications: ['communications.notifications.read'],
  offboarding: ['employee.offboarding.read'],
  onboarding: ['employee.onboarding.read', 'employee.onboarding.update'],
  'onboarding-guide': ['employee.onboarding.read', 'employee.onboarding.update'],
  payslips: [],
  policies: ['policy.documents.read'],
  projects: ['projects.portfolio.read'],
  regularization: [
    'attendance.regularization.read',
    'attendance.regularization.create',
    'attendance.regularization.update',
  ],
  reports: ['reporting.definitions.read', 'analytics.insights.read'],
  requisitions: ['recruitment.openings.read'],
  rewards: ['engagement.programs.read'],
  settings: ['platform.settings.read'],
  shifts: ['attendance.policy.read', 'attendance.policy.update'],
  surveys: ['engagement.surveys.read'],
  tickets: ['support.tickets.read'],
  timesheets: ['projects.resources.read'],
  training: ['learning.programs.read'],
  webhooks: ['integrations.webhooks.read'],
};

// Merge added entries into PAGE_PERMISSIONS if not already present
for (const [k, v] of Object.entries(ADDED_PAGE_PERMISSIONS)) {
  if (PAGE_PERMISSIONS[k] === undefined) {
    // @ts-ignore - intentionally extending the map
    PAGE_PERMISSIONS[k] = v;
  }
}

/**
 * Whether the user can view a given page.
 * - Admins bypass
 * - Public self-service pages always allowed
 * - Unmapped pages are denied
 * - Otherwise ANY of the mapped permission codes is required
 */
export function canViewPage(
  pageKey: string,
  ctx: { permissions: string[]; isAdmin: boolean },
): boolean {
  if (ctx.isAdmin) return true;
  if (PUBLIC_SELF_SERVICE_PAGES.has(pageKey)) return true;
  const required = PAGE_PERMISSIONS[pageKey];
  if (required === undefined) return false;
  if (required.length === 0) return PUBLIC_SELF_SERVICE_PAGES.has(pageKey);
  return hasAnyPermission(ctx.permissions, required);
}
